/**
 * Segment Service (Web)
 *
 * Resolves the segment-aware UI label for the Job/Project entity.
 * Per docs/architecture/project-entity.md §3.1 — Option (b) decision.
 *
 * The "Job" vs "Project" label is a presentational concern only.
 * Internal entity names, DB keys, event types, audit log strings, and
 * analytics dimensions stay 'JOB_*' permanently (one canonical name for
 * reporting — per architecture doc §3.1 step 2).
 *
 * Public API:
 *   COMPANY_SEGMENTS — frozen enum of valid segment strings
 *   resolveJobLabel(segment) → 'Project' | 'Job'
 *   CONTRACTOR_SEGMENTS — frozenset of segments that produce 'Project'
 *
 * Mirror: mobile/src/services/segmentService.js (identical public API).
 */

// ─── Segment enum ─────────────────────────────────────────────────────────────

/**
 * COMPANY_SEGMENTS
 *
 * Frozen list of valid segment values for companies/{cid}/settings/segment.
 * Must stay in sync with database.rules.json .validate on settings/segment.
 */
const COMPANY_SEGMENTS = Object.freeze([
  'contractor',
  'staffing',
  'trucking',
  'field_service',
  'logistics',
  'cleaning',
  'warehouse',
  'general',
]);

/**
 * CONTRACTOR_SEGMENTS
 *
 * The set of segment values that present the Job entity as "Project" to
 * admin-role users in admin surfaces. Field workers (role: 'user') always
 * see "Job" regardless of segment — that gate is enforced by resolveJobLabel.
 *
 * Per project-entity.md §3.1: contractor | construction | remodeling | hvac |
 * landscaping segments → 'Project'. The canonical DB enum is 'contractor';
 * industry aliases (hvac, landscaping, etc.) are resolved to 'contractor'
 * during onboarding and stored as 'contractor'.
 */
const CONTRACTOR_SEGMENTS = Object.freeze(new Set(['contractor']));

// ─── Label resolver ──────────────────────────────────────────────────────────

/**
 * resolveJobLabel(segment)
 *
 * Pure function. Returns the correct label for the Job entity given the
 * company's segment string.
 *
 * Mapping (per project-entity.md §3.1):
 *   'contractor'   → 'Project'  (residential GC, remodeling, HVAC, landscaping tenants)
 *   all others     → 'Job'
 *
 * NOTE: This function does NOT check the viewer's role. The caller is
 * responsible for passing 'general' (or any non-contractor segment) when the
 * viewer is a field worker (role: 'user'), because field workers always see
 * "Job" regardless of segment. The entityLabels helper in src/utils/entityLabels.js
 * wraps this with the role gate.
 *
 * Do NOT use this label in:
 *   - DB keys or paths (always 'job')
 *   - Event type strings (always 'JOB_*')
 *   - Audit log strings (always 'job')
 *   - Analytics dimensions (always 'job')
 *
 * @param {string} segment - one of COMPANY_SEGMENTS values, or any unknown string
 * @returns {'Project'|'Job'}
 */
function resolveJobLabel(segment) {
  if (typeof segment === 'string' && CONTRACTOR_SEGMENTS.has(segment)) {
    return 'Project';
  }
  return 'Job';
}

module.exports = { COMPANY_SEGMENTS, CONTRACTOR_SEGMENTS, resolveJobLabel };
